import { Line } from "react-chartjs-2";
import { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { format } from "date-fns";
import { RiceDataItem } from "../types/report.types";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface ChartWithTransactionsProps {
  riceData: RiceDataItem[];
}

type FontWeight = "normal" | "bold" | "lighter" | "bolder";

const ChartWithTransactions = ({ riceData = [] }: ChartWithTransactionsProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const labels = riceData.map((item) =>
    format(new Date(item.time), "dd/MM/yyyy")
  );
  const productNames = Object.keys(riceData[0]?.products || {});

  const handleClick = (elements: any) => {
    if (elements.length > 0) {
      const dataIndex = elements[0].index;
      setSelectedDate(riceData[dataIndex].time);
    }
  };

  const transactions = selectedDate
    ? riceData.find((item) => item.time === selectedDate)?.transactions || []
    : [];

  const datasets = productNames.map((productName, index) => ({
    label: productName,
    data: riceData.map((item) => item.products[productName].stock),
    borderColor: getLineColor(index),
    backgroundColor: getLineColor(index),
    tension: 0.1,
    borderWidth: 2,
    pointRadius: (ctx: any) => {
      const dataIndex = ctx.dataIndex;
      return riceData[dataIndex].transactions.length > 0 ? 6 : 0;
    },
    pointHoverRadius: 8,
    pointBackgroundColor: getLineColor(index),
    pointBorderColor: getLineColor(index),
    pointBorderWidth: 2,
    pointHoverBackgroundColor: getLineColor(index),
    pointHoverBorderColor: "#fff",
    pointHoverBorderWidth: 2,
  }));

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          padding: 20,
          font: {
            size: window.innerWidth < 640 ? 12 : 14,
            weight: "normal" as FontWeight,
          },
          usePointStyle: true,
          boxWidth: window.innerWidth < 640 ? 6 : 8,
        },
      },
      title: {
        display: true,
        text: t("chart.title"),
        font: {
          size: window.innerWidth < 640 ? 16 : 18,
          weight: "bold" as FontWeight,
        },
        padding: { bottom: 20 },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#000",
        bodyColor: "#000",
        bodyFont: {
          size: 13,
        },
        padding: 12,
        borderColor: "rgba(0, 0, 0, 0.1)",
        borderWidth: 1,
        callbacks: {
          title: (tooltipItems: any) => {
            return format(
              new Date(riceData[tooltipItems[0].dataIndex].time),
              "dd/MM/yyyy"
            );
          },
          label: (context: any) => {
            return `${context.dataset.label}: ${context.parsed.y} ${t(
              "chart.units"
            )}`;
          },
          afterBody: (context: any) => {
            const dataIndex = context[0].dataIndex;
            const transactions = riceData[dataIndex].transactions;
            if (transactions.length === 0) return "";

            return [
              "",
              "Transactions:",
              ...transactions.map(
                (t) => `${format(new Date(t.time), "HH:mm")} - ${t.type}`
              ),
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("chart.yAxis"),
          font: {
            size: window.innerWidth < 640 ? 12 : 14,
            weight: "normal",
          },
          padding: { bottom: 10 },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
        ticks: {
          font: {
            size: window.innerWidth < 640 ? 11 : 12,
          },
          padding: 8,
        },
      },
      x: {
        title: {
          display: true,
          text: t("chart.xAxis"),
          font: {
            size: window.innerWidth < 640 ? 12 : 14,
            weight: "normal",
          },
          padding: { top: 10 },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: window.innerWidth < 640 ? 11 : 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    onClick: (_event: any, elements: any) => handleClick(elements),
  };

  return (
    <div className="w-full p-2 sm:p-4 bg-base-100">
      <div className="h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] card bg-base-200 shadow-xl p-2 sm:p-4">
        <Line
          options={options as ChartOptions<'line'>}
          data={{ labels, datasets }}
        />
      </div>

      {selectedDate && (
        <div className="mt-4 sm:mt-8 card bg-base-200 shadow-xl">
          <div className="card-body p-2 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h3 className="card-title text-lg sm:text-xl md:text-2xl break-all">
                {t("transactions.title", {
                  date: format(new Date(selectedDate), "dd/MM/yyyy"),
                })}
              </h3>
              <div className="badge badge-lg">
                {t("transactions.count", { count: transactions.length })}
              </div>
            </div>

            {transactions.length > 0 ? (
              <div className="space-y-4">
                {/* Desktop Table - Hidden on Mobile */}
                <div className="hidden sm:block overflow-x-auto rounded-box">
                  <table className="table table-pin-rows border border-base-300">
                    <thead>
                      <tr className="border-b border-base-300">
                        <th className="text-base border-r border-base-300">
                          {t("transactions.table.time")}
                        </th>
                        <th className="text-base border-r border-base-300">
                          {t("transactions.table.type")}
                        </th>
                        <th className="text-base border-r border-base-300">
                          {t("transactions.table.store")}
                        </th>
                        <th className="text-base">
                          {t("transactions.table.products")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t, i) => (
                        <tr
                          key={i}
                          className="hover:bg-base-300 transition-colors duration-200 border-b border-base-300"
                        >
                          <td className="font-medium border-r border-base-300">
                            {format(new Date(t.time), "HH:mm:ss")}
                          </td>
                          <td className="border-r border-base-300">
                            <span
                              className={`badge ${
                                t.type === "IN"
                                  ? "badge-success text-success-content"
                                  : "badge-error text-error-content"
                              } font-semibold`}
                            >
                              {t.type}
                            </span>
                          </td>
                          <td className="border-r border-base-300">
                            <div className="badge badge-neutral badge-outline">
                              {t.storeCode}
                            </div>
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(t.products).map(
                                ([product, value]) => (
                                  <div
                                    key={product}
                                    className="badge badge-md bg-base-300 hover:bg-base-200 transition-colors duration-200 cursor-help"
                                    title={`${product}: ${value} units`}
                                  >
                                    <span className="font-medium mr-1">
                                      {product}
                                    </span>
                                    <span className="opacity-75">
                                      {value > 0 ? `+${value}` : value}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards - Shown only on Mobile */}
                <div className="sm:hidden space-y-4">
                  {transactions.map((item, i) => (
                    <div
                      key={i}
                      className="card bg-base-300 shadow-sm border border-base-200"
                    >
                      <div className="card-body p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-semibold">
                            {format(new Date(item.time), "HH:mm:ss")}
                          </span>
                          <span
                            className={`badge ${
                              item.type === "IN"
                                ? "badge-success text-success-content"
                                : "badge-error text-error-content"
                            } font-semibold`}
                          >
                            {item.type}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm opacity-75">
                            {t("transactions.mobile.store")}
                          </span>
                          <div className="badge badge-neutral badge-outline">
                            {item.storeCode}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <span className="text-sm opacity-75">
                            {t("transactions.mobile.products")}
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(item.products).map(
                              ([product, value]) => (
                                <div
                                  key={product}
                                  className="badge badge-md bg-base-200 hover:bg-base-100 
                                          transition-colors duration-200 cursor-help"
                                  title={`${product}: ${value} units`}
                                >
                                  <span className="font-medium mr-1">
                                    {product}
                                  </span>
                                  <span className="opacity-75">
                                    {value > 0 ? `+${value}` : value}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="alert alert-info shadow-lg text-sm sm:text-base">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  className="stroke-current shrink-0 w-5 h-5 sm:w-6 sm:h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{t("transactions.noData")}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const getLineColor = (index: number) => {
  const colors = [
    "#2563eb", // Blue
    "#dc2626", // Red
    "#16a34a", // Green
    "#9333ea", // Purple
    "#ea580c", // Orange
    "#0891b2", // Cyan
  ];
  return colors[index % colors.length];
};

export default ChartWithTransactions;
